/**
 * Complex geometry classes that use the basic geometry classes
 * Point set, path, trace and polygon
 * Definitions can be found below
 */

var Geometry = require("./basicGeometry.js");

/**
 * Path is a svg-like line composed of straight sections, arcs, bezier curves, ...
 * @param {array} path svg style path objects
 */
Geometry.Path = function(path) {
    if(!path) path = [];
    this.path = path;
    this.filled = false;
};

Geometry.Path.prototype.moveTo = function(x, y, relative) {
    this.path.push([relative ? "m" : "M", x, y]);
    return this;
};

Geometry.Path.prototype.lineTo = function(x, y, relative) {
    this.path.push([relative ? "l" : "L", x, y]);
    return this;
};

Geometry.Path.prototype.arcTo = function(rx, ry, x_axis_rotation, large_arc, sweep_flag, x, y, relative) {
    this.path.push([relative ? "a" : "A", rx, ry, x_axis_rotation, large_arc, sweep_flag, x, y]);
    return this;
};

Geometry.Path.prototype.fromEllipse = function(rx, ry) {
    this.moveTo(0, -ry, true)
        .arcTo(rx, ry, 0, 1, 1, 0, 2 * ry, true)
        .arcTo(rx, ry, 0, 0, 1, 0, -2 * ry, true)
        .close();
    return this;
};

Geometry.Path.prototype.curveTo = function(x1, y1, x2, y2, x, y, relative) {
    this.path.push([relative ? "c" : "C", x1, y1, x2, y2, x, y]);
    return this;
};

Geometry.Path.prototype.close = function() {
    this.path.push(['Z']);
    return this;
};

Geometry.Path.prototype.fill = function(fill) {
    this.filled = !!fill;
    return this;
};

Geometry.Path.prototype._toGeometry = function() {
    return this.path.map(function(segment) {
        switch(segment[0]) {
            case 'a':
            case 'A':
                return [segment[0], segment[1], segment[2], segment[3],
                    segment[4], segment[5], new Geometry.Coord(segment[6], segment[7])];
            case 'M':
            case 'm':
            case 'l':
            case 'L':
                return [segment[0], new Geometry.Coord(segment[1], segment[2])];
            case 'c':
            case 'C':
                return [segment[0], new Geometry.Coord(segment[1], segment[2]),
                    new Geometry.Coord(segment[3], segment[4]), new Geometry.Coord(segment[5], segment[6])];
            case 'z':
            case 'Z':
                return segment;
            default:
                throw new Error("unknown path object: "+segment[0]);
        }
    });
};

Geometry.Path.prototype._fromGeometry = function(path) {
    this.path = path.map(function(segment) {
        switch(segment[0]) {
            case 'a':
            case 'A':
                return [segment[0], segment[1], segment[2], segment[3],
                    segment[4], segment[5], segment[6].x, segment[6].y];
            case 'm':
            case 'M':
            case 'l':
            case 'L':
                return [segment[0], segment[1].x, segment[1].y];
            case 'c':
            case 'C':
                return [segment[0], segment[1].x, segment[1].y,
                    segment[2].x, segment[2].y, segment[3].x, segment[3].y];
            case 'z':
            case 'Z':
                return segment;
            default:
                throw new Error("unknown path object: "+segment[0]);
        }
    });
    return this;
};

Geometry.Path.prototype.rotate = function(angle, center) {
    return new Geometry.Path()._fromGeometry(this._toGeometry().map(function(segment) {
        switch(segment[0]) {
            case 'z':
            case 'Z':
                break;
            case 'm':
            case 'l':
                segment[1] = segment[1].rotate(angle);
                break;
            case 'a':
                segment[3] += angle;
                segment[6] = segment[6].rotate(angle);
                break;
            case 'c':
                segment[1] = segment[1].rotate(angle);
                segment[2] = segment[2].rotate(angle);
                segment[3] = segment[3].rotate(angle);
                break;
            case 'L':
            case 'M':
                segment[1] = segment[1].rotate(angle, center);
                break;
            case 'A':
                segment[3] += angle;
                segment[6] = segment[6].rotate(angle, center);
                break;
            case 'C':
                segment[1] = segment[1].rotate(angle, center);
                segment[2] = segment[2].rotate(angle, center);
                segment[3] = segment[3].rotate(angle, center);
                break;
            default:
                throw new Error("unknown path object: "+segment[0]);
        }
        return segment;
    }));
};

Geometry.Path.prototype.translate = function(vec) {
    return new Geometry.Path()._fromGeometry(this._toGeometry().map(function(segment) {
        switch(segment[0]) {
            case 'a':
            case 'l':
            case 'm':
            case 'c':
            case 'z':
            case 'Z':
                break;
            case 'L':
            case 'M':
                segment[1] = segment[1].translate(vec);
                break;
            case 'A':
                segment[6] = segment[6].translate(vec);
                break;
            case 'C':
                segment[1] = segment[1].translate(vec);
                segment[2] = segment[2].translate(vec);
                segment[3] = segment[3].translate(vec);
                break;
            default:
                throw new Error("unknown path object: "+segment[0]);
        }
        return segment;
    }));
};

Geometry.Path.prototype.toAbsolutePath = function() {
    var prev = new Geometry.Coord(0, 0);
    this._fromGeometry(this._toGeometry().map(function(segment) {
        switch(segment[0]) {
            case 'a':
                segment[0] = segment[0].toUpperCase();
                segment[6] = segment[6].add(prev);
                prev = segment[6];
                break;
            case 'l':
            case 'm':
                segment[0] = segment[0].toUpperCase();
                segment[1] = segment[1].add(prev);
                prev = segment[1];
                break;
            case 'c':
                segment[0] = segment[0].toUpperCase();
                segment[1] = segment[1].add(prev);
                segment[2] = segment[2].add(prev);
                segment[3] = segment[3].add(prev);
                prev = segment[3];
                break;
            case 'L':
            case 'M':
                prev = segment[1];
                break;
            case 'A':
                prev = segment[6];
                break;
            case 'C':
                prev = segment[3];
                break;
            case 'z':
            case 'Z':
                break;
            default:
                throw new Error("unknown path object: "+segment[0]);
        }
        return segment;
    }));
    return this;
};

Geometry.Path.prototype.approximatePoints = function(precision) {
    var points = [];
    var abs_path = this.toAbsolutePath().path;
    var prev = null;
    for (var i=0; i<abs_path.length; i++) {
        var  segment = abs_path[i];
        switch(segment[0]) {
            case "M":
                prev = new Geometry.Coord(segment[1], segment[2]);
                if (abs_path.length > i + 1 && abs_path[i+1][0] !== "M") {
                    points.push(prev);
                }
                break;
            case "L":
                prev = new Geometry.Coord(segment[1], segment[2]);
                points.push(prev);
                break;
            case 'z':
            case "Z":
                prev = points[0];
                points.push(prev);
                break;
            case "C":
                points = points.concat(Geometry.bezierToPolygonApproximation(prev, segment, precision));
                prev = points[points.length-1];
                break;
            case "A":
                points = points.concat(Geometry.arcToPolygonApproximation(prev, segment, precision));
                prev = points[points.length-1];
                break;
            default:
                throw new Error("Unknown segment type");
        }
    }
    return points;
};

Geometry.Path.prototype.toPolygon = function(precision) {
    var points = this.approximatePoints(precision);
    if(points[0] == points[points.length-1])    //polygon is considered closed anyway
        points.pop(points.length-1);
    return new Geometry.Polygon(points);
};

Geometry.Path.prototype.toTrace = function(precision) {
    return new Geometry.Trace(this.approximatePoints(precision));
};

Geometry.Path.prototype.toSegmented = function(precision) {
    if(this.filled)
        return this.toPolygon(precision);
    else
        return this.toTrace(precision);
};

Geometry.Path.prototype.getBB = function() {
    var abs_path = this.toAbsolutePath().path;
    var prev = null;
    var first = null;
    var bb = Geometry.BoundingBox.emptyBoundingBox;
    for (var i=0; i<abs_path.length; i++) {
        var  segment = abs_path[i];
        switch(segment[0]) {
            case "M":
                prev = new Geometry.Coord(segment[1], segment[2]);
                if (abs_path.length > i + 1 && abs_path[i+1][0] !== "M") {
                    bb = bb.merge(prev.getBB());
                }
                break;
            case "L":
                prev = new Geometry.Coord(segment[1], segment[2]);
                bb = bb.merge(prev.getBB());
                break;
            case 'z':
            case "Z":
                prev = first;
                bb = bb.merge(prev.getBB());
                break;
            case "C":
                bb = bb.merge(Geometry.bezierToBoundingBox(prev, segment));
                prev = new Geometry.Coord(segment[5], segment[6]);
                break;
            case "A":
                bb = bb.merge(Geometry.arcToBoundingBoxApproximation(prev, segment));
                prev = new Geometry.Coord(segment[6], segment[7]);
                break;
            default:
                throw new Error("Unknown segment type");
        }
        if(!first) first = prev;
    }
    return bb;
};

Geometry.Path.prototype.toString = function() {
    return this._toGeometry().toString();
};

Geometry.Path.prototype.toSVG = function() {
    return this.path.reduce(function(prev, cur) {
        return prev.concat(cur);
    }, []).join(" ");
};

/**
 * A pointset is a set of points. Points are not connected or in any particular order.
 * @param   {Array}    points
 */
Geometry.PointSet = function(points) {
    var self = this;
    self.points = [];
    if (points !== undefined && points.length !== 0) {
        if (points[0].constructor === Geometry.Coord) {
            for (var i=0; i<points.length; i++) {
                this.points.push(points[i]);
            }
        } else if (points[0].x !== undefined && points[0].y !== undefined) {
            for (var i=0; i<points.length; i++) {
                this.points.push(new Geometry.Coord(points[i].x,points[i].y));
            }
        } else if (points[0].constructor === Array) {
            for (var i=0; i<points.length; i++) {
                this.points.push(new Geometry.Coord(points[i][0],points[i][1]));
            }
        } else {
            // throw new Error("unknown pointset argument"); //TODO reenable after lib-core refactoring
        }
    }
};

Geometry.PointSet.prototype.taintCache = function() {
    this._bb = null;
};

Geometry.PointSet.prototype.copy = function() {
    return new this.constructor(this.points);
};

Geometry.PointSet.prototype.toString = function() {
    return this.points.map(function(p) {return p.toString();}).join(',');
};

Geometry.PointSet.prototype.add = function(point){
    this.taintCache();
    this.points.push(point);
};

Geometry.PointSet.prototype.round = function(precision) {
    var result = new this.constructor();
    for (var i = 0; i < this.points.length; i++) {
        result.points.push(this.points[i].round(precision));
    }
    return result;
};

Geometry.PointSet.prototype.scale = function(facX,facY) {
    if(facX === 1 && facY === 1) return this; //no change
    var result = new this.constructor();
    for (var i = 0; i < this.points.length; i++) {
        result.points.push(this.points[i].mult(facX,facY));
    }
    return result;
};

Geometry.PointSet.prototype.flip = function(flip){
    return this.scale(flip,1);
};

Geometry.PointSet.prototype.translate = function(vec) {
    if(vec.x === 0 && vec.y === 0) return this; //no change
    var result = new this.constructor();
    for (var i = 0; i < this.points.length; i++) {
        result.points.push(this.points[i].add(vec));
    }
    return result;
};

Geometry.PointSet.prototype.rotate = function(angle, center) {
    if(angle === 0) return this;
    var result = new this.constructor();
    for (var i = 0; i < this.points.length; i++) {
        result.points.push(this.points[i].rotate(angle, center));
    }
    return result;
};

Geometry.PointSet.prototype.transform = function(transform){
    var result = new this.constructor();
    for (var i = 0; i < this.points.length; i++) {
        result.points.push(this.points[i].transform(transform));
    }
    return result;
};

Geometry.PointSet.prototype._getBB = function() {
    var xMin = Number.POSITIVE_INFINITY;
    var xMax = Number.NEGATIVE_INFINITY;
    var yMin = Number.POSITIVE_INFINITY;
    var yMax = Number.NEGATIVE_INFINITY;

    for (var i=0; i<this.points.length; i++){
        if (this.points[i].x<xMin) xMin = this.points[i].x;
        if (this.points[i].x>xMax) xMax = this.points[i].x;
        if (this.points[i].y<yMin) yMin = this.points[i].y;
        if (this.points[i].y>yMax) yMax = this.points[i].y;
    }
    return new Geometry.BoundingBox.fromCoordinates(xMin, yMin, xMax, yMax);
};

Geometry.PointSet.prototype.getBB = function() {
    if(!this._bb) this._bb = this._getBB();
    return this._bb;
};

Geometry.PointSet.prototype.centroid = function() {
    var sum = new Geometry.Coord(0,0);
    for (var i=0; i<this.points.length; i++){
        var point = this.points[i];
        sum = sum.add(point);
    }
    return sum.mult(1/this.points.length);
};

Geometry.PointSet.prototype.containedByBB = function(bb) {
    for (var i=0; i<this.points.length; i++){
        if(!this.points[i].containedByBB(bb))
            return false;
    }
    return this.points.length > 0;
};

Geometry.PointSet.prototype.intersectsBB = function(bb) {
    for (var i = 0; i < this.points.length; i++) {
        if(bb.contains(this.points[i])) {
            return true;
        }
    }
    return false;
};


/**
 * A trace is a list of connected points, each consecutive two points connected using a segment.
 * @constructor
 * @param   {Array}    points
 */
Geometry.Trace = function(points) {
    Geometry.PointSet.call(this, points);
};

Geometry.inherit(Geometry.Trace, Geometry.PointSet);

Geometry.Trace.prototype.taintCache = function() {
    Geometry.PointSet.prototype.taintCache.call(this);
    this._segments = null;
};

Geometry.Trace.prototype.close = function() {
    this.add(this.points[0]);
    return this;
};

Geometry.Trace.prototype.eq = function(p) {
    if (this.points.length !== p.points.length) return false;

    for (var j =0; j < p.points.length; j++) {
        if (!this.points[j].eq(p.points[j])) {
            return false;
        }
    }
    return true;
};

Geometry.Trace.prototype.toPath = function() {
    var path = [];
    if(this.points.length !== 0) {
        path.push(['M', this.points[0].x, this.points[0].y]);
        for (var i=1; i<this.points.length; i++) {
            var  point = this.points[i];
            path.push(['L', point.x, point.y]);
        }
    }
    return new Geometry.Path(path);
};

Geometry.Trace.prototype._getSegments = function(){
    var result = [];
    for (var i = 1; i < this.points.length; i++) {
        result.push(new Geometry.Segment(this.points[i-1],this.points[i]));
    }
    return result;
};

Geometry.Trace.prototype.segments = function(){
    if(!this._segments) this._segments = this._getSegments();
    return this._segments;
};

Geometry.Trace.prototype.intersects = function(obj) {
    switch(obj.constructor) {
        case Geometry.Polygon:
        case Geometry.Trace:
            return Geometry.any(this.segments(), function(s0){
                return Geometry.any(obj.segments(), function(s1){
                    return s0.intersects(s1);
                });
            });
        case Geometry.Segment:
            return Geometry.any(this.segments(), function(s0){
                return s0.intersects(obj);
            });
        case Geometry.BoundingBox:
            return this.intersectsBB(obj);
        default:
            throw new Error("Unknown intersection object");
    }
};

Geometry.Trace.prototype.intersectsBB = function(bb) {
    return Geometry.any(this.segments(), function(s0){
        return s0.intersectsBB(bb);
    });
};

Geometry.Trace.prototype.closeTo = function(obj, dist) {
    var self = this;
    switch(obj.constructor) {
        case Geometry.Polygon:
        case Geometry.Trace:
            return Geometry.all(this.points, function(p0){
                return Geometry.any(obj.segments(), function(s1){
                    return s1.closeTo(p0, dist);
                });
            })
            && Geometry.all(obj.points, function(p0){
                return Geometry.any(self.segments(), function(s1){
                    return s1.closeTo(p0, dist);
                });
            });
        default:
            throw new Error("Unknown intersection object");
    }
};

Geometry.Trace.prototype.fastIntersectsBB = Geometry.PointSet.prototype.intersectsBB;

Geometry.Trace.prototype.toSegmented = function() {
    return this;
};


var oldIntersection = Geometry.Segment.prototype.intersection;
Geometry.Segment.prototype.intersection = function (obj) {
    var self = this;
    switch(obj.constructor) {
        case Geometry.Polygon:
            var intersections = [];
            obj.segments().forEach(function (seg) {
                var intersection = self.intersection(seg); // TODO
                if(intersection) intersections.push(intersection);
            });
            intersections = intersections.sort(function (a, b) {
                return self.parameter(a) - self.parameter(b);
            });
            intersections.unshift(this.a);
            intersections.push(this.b);
            var start = obj.contains(intersections[0]) ? 0 : 1;
            var res = [];
            for(var i = start+1; i<intersections.length; i+=2) {
                var p1 = intersections[i-1];
                var p2 = intersections[i];
                res.push(new Geometry.Segment(p1, p2));
            }
            return res;
        default:
            return oldIntersection.call(this, obj);
    }
};

Geometry.Segment.prototype.toTrace = function () {
    return new Geometry.Trace([this.a, this.b]);
};

Geometry.Segment.prototype.toPolygon = function (width, halfCircleSegments) {
    halfCircleSegments = halfCircleSegments || 16;
    width = width || 1;
    var u_p = !this.a.eq(this.b) ? this.a.sub(this.b).perp().norm() : new Geometry.Coord(0,1);
    var angle = 180/halfCircleSegments;
    var vec = u_p.mult(-1*width/2);
    var pointList = [];
    for(var i=0; i<halfCircleSegments+1; i++) {
        pointList.push(this.a.add(vec.rotate(i*angle)));
    }
    var start = !this.a.eq(this.b) ? 0 : 1;
    for(var i=start; i<halfCircleSegments+1-start; i++) {
        pointList.push(this.b.add(vec.rotate(i*angle+180)));
    }
    return new Geometry.Polygon(pointList);
};

/**
 * A polygon is a closed and filled shape defined by a list of connected points, each consecutive two points connected using a segment (and the first and last point connected using a segment too).
 * The first and last point of the polygon do not have to be the same for the polygon to be considered closed.
 * @param   {Array}    points
 */
Geometry.Polygon = function(points) {
    Geometry.Trace.call(this, points);
    var ded = []
    for(var i=1; i<this.points.length+1; i++) {
        if(!this.point(i-1).eq(this.point(i)))
            ded.push(this.point(i));
    }
    this.points = ded;
};

Geometry.inherit(Geometry.Polygon, Geometry.Trace);

Geometry.Polygon.prototype.taintCache = function() {
    Geometry.Trace.prototype.taintCache.call(this);
    this._polySegments = null;
};

Geometry.Polygon.prototype.point = function(i) {
    i = i >= this.points.length ? i - this.points.length : i;
    i = i < 0 ? i + this.points.length : i;
    console.assert(0 <= i && i <= this.points.length);
    return this.points[i];
};

Geometry.Polygon.prototype.eq = function(p) {
    if (this.points.length !== p.points.length) return false;

    var i = 0;
    while ( (i < this.points.length) && !this.points[i].eq(p.points[0])) i++;
    var i1 = i;
    for (var j1 = 0; j1 < p.points.length; j1++) {
        if (!this.point(i1).eq(p.points[j1])) {
            break;
        }
        i1++;
    }
    var i2 = i;
    for (var j2 = 0; j2 < p.points.length; j2++) {
        if (!this.point(i2).eq(p.points[j2])) {
            break;
        }
        i2--;
    }
    return j1 == p.points.length || j2 == p.points.length;
};

Geometry.Polygon.prototype.toPath = function() {
    var path = Geometry.Trace.prototype.toPath.call(this);
    path = path.close();
    return path;
};

Geometry.Polygon.prototype.toTrace = function() {
    var trace = new Geometry.Trace(this.points);
    trace = trace.close();
    return trace;
};

Geometry.Polygon.prototype._getPolySegments = function(){
    var traceSegments = Geometry.Trace.prototype.segments.call(this);
    var segments = traceSegments.slice();
    segments.push(new Geometry.Segment(this.points[this.points.length-1],this.points[0]));
    return segments;
};

Geometry.Polygon.prototype.segments = function(){
    if(!this._polySegments) this._polySegments = this._getPolySegments();
    return this._polySegments;
};

Geometry.Polygon.prototype.intersectsBoundary = Geometry.Trace.prototype.intersects;

Geometry.Polygon.prototype.intersects = function(obj) {
    var self = this;
    if(this.points.length == 0) return false;
    switch(obj.constructor) {
        case Geometry.Polygon:
            if(obj.points.length == 0) return false;
            if(!this.getBB().intersects(obj.getBB())) return false;
            return this.contains(obj.points[0]) || obj.contains(this.points[0]) || this.intersectsBoundary(obj);
        case Geometry.ComplexPolygon:
            var polys = [obj.getSimplePolygon()].concat(obj.getHolePolygons());
            return Geometry.any(polys, function (poly) {
                return self.intersects(poly);
            });
        case Geometry.MultiPolygon:
            return Geometry.any(obj.polygons, function (poly) {
                return self.intersects(poly);
            });
        case Geometry.Trace:
            if(obj.points.length == 0) return false;
            if(!this.getBB().intersects(obj.getBB())) return false;
            return this.contains(obj.points[0]) || this.intersectsBoundary(obj);
        case Geometry.Segment:
            return this.contains(obj.a) || this.intersectsBoundary(obj);
        case Geometry.BoundingBox:
            if(!this.getBB().intersects(obj)) return false;
            return this.intersectsBB(obj);
        default:
            console.error("Unknown intersection object");
    }
};

Geometry.Polygon.prototype.intersectsBB = function(bb) {
    return this.contains(bb.getMinMin()) || Geometry.Trace.prototype.intersectsBB.call(this, bb);
};

//experimental function
var stats = {count:0, poly_size:0, full: 0};
Geometry.Polygon.prototype.contains = function(obj) {
    var self = this;
    switch(obj.constructor) {
        case Geometry.Coord:
            stats.count += 1;
            stats.poly_size += this.points.length;
            if(!this.getBB().contains(obj)) return false;
            stats.full += 1;
            return this.containsCoord(obj);
        case Geometry.Segment:
            if(!this.getBB().contains(obj)) return false;
            return this.containsCoord(obj.a) && !this.intersectsBoundary(obj);
        case Geometry.Trace:
        case Geometry.Polygon:
            if(!this.getBB().contains(obj.getBB())) return false;
            if(this.contains(obj.getBB())) return true;
            return this.containsCoord(obj.points[0]) && !this.intersectsBoundary(obj);
        case Geometry.ComplexPolygon:
            return this.contains(obj.getSimplePolygon());
        case Geometry.MultiPolygon:
            var contains = function (poly) { return self.contains(poly); };
            return Geometry.all(obj.polygons, contains) && obj.polygons.length;
        case Geometry.BoundingBox:
            var poly = obj.getPoly();
            if(!this.getBB().contains(obj)) return false;
            return this.containsCoord(poly.points[0]) && !this.intersectsBoundary(poly);
        default:
            throw new Error("Unknown object");
    }
};

Geometry.Polygon.prototype.containsPoints = function(points) {
    var self = this;
    return Geometry.all(points, function(point) {
        return self.containsCoord(point);
    }) && points.length;
};


/**
 * Intersection counting algorithm with special case for point on boundary
 */
Geometry.Polygon.prototype.containsCoord = function(point) {
    var ray = new Geometry.Ray(Geometry.Line.HOR, point);
    var intersectionCount = 0;
    var onAnySegment = Geometry.any(this.segments(), function(segment) {
        // console.log(ray.specialSegmentIntersects(segment), segment)
        intersectionCount += ray.specialSegmentIntersects(segment);
        return segment.contains(point);
    });
    // console.log(onAnySegment);
    // console.log(intersectionCount);
    return onAnySegment || intersectionCount % 2 == 1;
};

/** Only for debug */
Geometry.Polygon.prototype.addToSVG = function(svg,group){
    var path = svg.createPath();

    path.move(this.points[0].x,this.points[0].y);
    for (var i = 1; i < this.points.length; i++) {
        path.line(this.points[i].x,this.points[i].y);
    }

    var result = svg.path(group, path);
    return result;
};

Geometry.Polygon.prototype.clockwise = function() {
    if(this.points.length === 0) return true;
    var extreme = 0;
    for (var i=1; i<this.points.length; i++) {
        if(this.points[extreme].order(this.points[i]) > 0) {
            extreme = i;
        }
    }
    return Geometry.clockwise(this.point(extreme-1), this.point(extreme), this.point(extreme+1));
};

Geometry.Polygon.prototype.fastBuffer = function(dist) {
    // This thing is a very dirty hack. It probably does not work in all situations
    function addIfNotToClose(points,p) {
        var minDist = dist/10;
        if (points.length == 0) {
            points.push(p);
            return;
        }

        var previous = points[points.length-1];
        if (previous.sub(p).length() > minDist) {
            points.push(p);
        }
        return;
    }

    var p = [];
    for (var i=0; i<this.points.length; i++) {
        p.push(this.points[i]);
    }

    var distance = this.clockwise() ? dist : -dist;
    // var distance = -dist;
    var buffered = [];
    p.push(p[0]);
    p.push(p[1]);

    for (var i=0; i < p.length-2; i++) {
        var p0 = p[i];
        var p1 = p[i+1];
        var p2 = p[i+2];

        var vec0 = p1.sub(p0).norm();
        var vec1 = p2.sub(p1).norm();

        var angle = Math.atan2(vec0.y,vec0.x) - Math.atan2(vec1.y,vec1.x);

        while (angle <= 0) angle += 2 * Math.PI;

        if (angle > Math.PI) {
            addIfNotToClose(buffered, p1.add(vec0.perp().mult(distance)));
            addIfNotToClose(buffered, p1.add(vec1.perp().mult(distance)));
        } else if (angle < Math.PI) {
            var line0 = new Geometry.Line(vec0,p1.add(vec0.perp().mult(distance)));
            var line1 = new Geometry.Line(vec1,p1.add(vec1.perp().mult(distance)));

            addIfNotToClose(buffered, line0.intersection(line1));
        }
    }

    return new Geometry.Polygon(buffered);
};

Geometry.Polygon.prototype.selfIntersects = function() {
    var segments = this.segments();
    for(var j = 2; j<segments.length-1; j++) {  //first segment naturally intersects with last segment
        if(segments[0].intersects(segments[j]))
            return true;
    }
    for(var i = 1; i<segments.length-1; i++) {
        for(j = i + 2; j<segments.length; j++) {//every segment naturally intersects with next segment
            if(segments[i].intersects(segments[j]))
                return true;
        }
    }
    return false;
};

Geometry.Polygon.prototype.toSVG = function() {
    var svg = '';
    if(this.points.length !== 0) {
        svg += 'M '+this.points[0].x+' '+this.points[0].y+' ';
        for (var i=1; i<this.points.length; i++) {
            svg += 'L '+this.points[i].x+' '+this.points[i].y+' ';
        }
        svg += 'Z';
    }
    return svg;
};

Geometry.Polygon.prototype.isRectangle = function() {
    if(this.points.length != 4) return false;
    var A=this.points[0], B=this.points[1], C=this.points[2], D=this.points[3];
    /**
     * A----B
     * |    |
     * D----C
     */
    if(Math.abs(B.distance(A) - C.distance(D)) > 0.000000001) return false;
    if(Math.abs(D.distance(A) - C.distance(B)) > 0.000000001) return false;
    if(Math.abs(B.sub(A).norm().dot(D.sub(A).norm())) > 0.000000001) return false;
    return {width: B.distance(A), height: D.distance(A), center: A.add(C).mult(1/2), angle: B.sub(A).angleDegrees()};
};

Geometry.Polygon.prototype.isOctagon = function() {
    if(this.points.length != 8) return false;
    var center = this.points[0].add(this.points[4]).mult(1/2);
    var radius = this.points[0].distance(center);
    var side = this.points[1].distance(this.points[0]);
    for(var i = 1; i<8; i++) {
        if(Math.abs(this.points[i].distance(center)-radius)>0.000000001) return false;
        if(Math.abs(this.points[i].distance(this.points[i-1])-side)>0.000000001) return false;
    }
    return {radius: radius, center: center, angle: this.points[0].sub(center).angleDegrees()};
};

Geometry.Polygon.prototype.draw = function(svg,group,style) {
    var path = svg.createPath();
    var rings = [];

    var ring = this.points.concat(this.points[0]);
    rings.push(ring);

    if(this.holes) {
        this.holes.forEach(function (hole) {
            var ring = hole.concat(hole[0]);
            rings.push(ring);
        });
    }

    for (var r = 0; r < rings.length; r++) {
        var ringCoords = rings[r];

        for (var i = 0; i < ringCoords.length-1; i++) {
            var point = ringCoords[i];

            if (i === 0) {
                path = path.move(point.x,point.y);
            } else {
                path = path.line(point.x,point.y);
            }
        }
        path=path.close();
    }

    return svg.path(group, path,style);
};

/**
 * A complex polygon is a polygon containing holes (each hole defined by a list of points)
 * A complex polygon is the set of points in the polygon minus the points in the hole polygons
 */
Geometry.ComplexPolygon = function(points, holes) {
    Geometry.Polygon.call(this, points);
    this.holes = holes || [];
};

Geometry.inherit(Geometry.ComplexPolygon, Geometry.Polygon);

Geometry.ComplexPolygon.prototype.taintCache = function() {
    Geometry.Polygon.prototype.taintCache.call(this);
    this._simplePolygon = null;
    this._holePolygons = null;
};

Geometry.ComplexPolygon.prototype.round = function(precision) {
    var result = Geometry.PointSet.prototype.round.call(this, precision);
    for (var i = 0; i < this.holes.length; i++) {
        var hole = this.holes[i];
        var rounded_hole = [];
        for (var j = 0; j < hole.length; j++) {
            rounded_hole.push(hole[j].round(precision));
        }
        result.holes.push(rounded_hole);
    }
    return result;
};

Geometry.ComplexPolygon.prototype.flip = function(flip){
    var holes = [];
    var myHolePolys = this.getHolePolygons();
    for(var i = 0; i < myHolePolys.length; i++){
        holes.push(myHolePolys[i].flip(flip).points);
    }
    var points = this.getSimplePolygon().flip(flip).points;
    return new Geometry.ComplexPolygon(points, holes);

};

Geometry.ComplexPolygon.prototype.intersects = function(obj) {
    var boundaryIntersection = Geometry.Polygon.prototype.intersects.call(this.getSimplePolygon(), obj);
    var contains = function (holePoly) {
        return holePoly.contains(obj);
    };
    return boundaryIntersection && !Geometry.any(this.getHolePolygons(), contains);
};

Geometry.ComplexPolygon.prototype.contains = function(obj) {
    var boundaryContains = Geometry.Polygon.prototype.contains.call(this.getSimplePolygon(), obj);
    var contains = function (holePoly) {
        return holePoly.contains(obj);
    };
    return boundaryContains && !Geometry.any(this.getHolePolygons(), contains);
};

Geometry.ComplexPolygon.prototype._getHolePolygons = function() {
    return this.holes.map(function(hole) {
        return new Geometry.Polygon(hole);
    });
};

Geometry.ComplexPolygon.prototype.getHolePolygons = function() {
    if(!this._holePolygons) this._holePolygons = this._getHolePolygons();
    return this._holePolygons;
};

Geometry.ComplexPolygon.prototype._getSimplePolygon = function() {
    return new Geometry.Polygon(this.points);
};

Geometry.ComplexPolygon.prototype.getSimplePolygon = function() {
    if(!this._simplePolygon) this._simplePolygon = this._getSimplePolygon();
    return this._simplePolygon;
};


Geometry.ComplexPolygon.prototype.translate = function(vec) {
    var result = new this.constructor();
    for (var i = 0; i < this.points.length; i++) {
        result.points.push(this.points[i].add(vec));
    }
    for (var i = 0; i < this.holes.length; i++) {
        result.holes.push([]);
        for (var j = 0; j < this.holes[i].length; j++) {
           result.holes[i].push(this.holes[i][j].translate(vec));
        }
    }
    return result;
};

Geometry.ComplexPolygon.prototype.rotate = function(angle, center) {
    var result = new this.constructor();
    for(var i = 0; i < this.points.length; i++){
         result.points.push(this.points[i].rotate(angle, center));
    }
    for (var i = 0; i < this.holes.length; i++) {
        result.holes.push([]);
        for (var j = 0; j < this.holes[i].length; j++) {
            result.holes[i].push(this.holes[i][j].rotate(angle, center));
        }
    }
    return result;
};


Geometry.ComplexPolygon.prototype.eq = function() {
    throw new Error("Not implemented");
};

/**
 * A multipolygon is a set of polygons
 */
Geometry.MultiPolygon = function(polygons) {
    var input = [];
    if(polygons) {
        polygons.forEach(function (poly) {
            if(poly instanceof Geometry.MultiPolygon) input = input.concat(poly.polygons);
            else input.push(poly);
        });
    }
    this.polygons = input;
};

Geometry.MultiPolygon.prototype.taintCache = function() {
    this._bb = null;
};

Geometry.MultiPolygon.prototype._getBB = function() {
    var bb = Geometry.BoundingBox.emptyBoundingBox;
    this.polygons.forEach(function(poly) {
        bb = bb.merge(poly.getBB());
    });
    return bb;
};

Geometry.MultiPolygon.prototype.getBB = function() {
    if(!this._bb) this._bb = this._getBB();
    return this._bb;
};

Geometry.MultiPolygon.prototype.containedByBB = function(bb) {
    var contains = function (poly) { return poly.containedByBB(bb); };
    return Geometry.any(this.polygons, contains);
};

Geometry.MultiPolygon.prototype.intersects = function(oth) {
    var intersects = function (poly) { return poly.intersects(oth); };
    return Geometry.any(this.polygons, intersects);
};

Geometry.MultiPolygon.prototype.contains = function(oth) {
    var contains = function (poly) { return poly.contains(oth); };
    return Geometry.any(this.polygons, contains);
};

Geometry.MultiPolygon.prototype.fastBuffer = function(dist) {
    var bufferedPolygons = this.polygons.map(function(poly) {
        return poly.fastBuffer(dist);
    });
    return new Geometry.MultiPolygon(bufferedPolygons);
};

Geometry.MultiPolygon.prototype.buffer = function(dist) {
    var bufferedPolygons = this.polygons.map(function(poly) {
        return poly.buffer(dist);
    });
    return new Geometry.MultiPolygon(bufferedPolygons);
};

Geometry.MultiPolygon.prototype.translate = function(vec) {
     var result = new this.constructor();
    for(var i = 0; i < this.polygons.length; i++){
        result.polygons.push(this.polygons[i].translate(vec));
    }
    return result;
};

Geometry.MultiPolygon.prototype.rotate = function(angle, center) {
     var result = new this.constructor();
    for(var i = 0; i < this.polygons.length; i++){
        result.polygons.push(this.polygons[i].rotate(angle, center));
    }
    return result;
};


module.exports = Geometry;
